import React, { useState, useEffect } from 'react';
import { db } from '../services/db';
import { TimeEntry, Project } from '../models/types';
import { autoSync } from '../services/syncService';
import '../styles/ClockControls.css';

interface ClockControlsProps {
  onStatusChange: () => void;
}

const ClockControls: React.FC<ClockControlsProps> = ({ onStatusChange }) => {
  const [currentEntry, setCurrentEntry] = useState<TimeEntry | null>(null);
  const [projects, setProjects] = useState<Project[]>([]);
  const [selectedProject, setSelectedProject] = useState<string>('');
  const [selectedTask, setSelectedTask] = useState<string>('');
  const [location, setLocation] = useState<string>('Site');
  const [tasks, setTasks] = useState<string[]>([]);
  const [notes, setNotes] = useState<string>('');
  const [showLocation, setShowLocation] = useState(true);

  useEffect(() => {
    loadData();
    
    // Load settings
    const storedShowLocation = localStorage.getItem('showLocationButtons');
    if (storedShowLocation !== null) {
      setShowLocation(storedShowLocation === 'true');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (selectedProject) {
      const project = projects.find(p => p.name === selectedProject);
      if (project) {
        setTasks(project.tasks);
      } else {
        setTasks([]);
      }
    } else {
      setTasks([]);
    }
  }, [selectedProject, projects]);

  const loadData = async () => {
    try {
      const currentTimeEntry = await db.getCurrentTimeEntry();
      setCurrentEntry(currentTimeEntry || null);

      const allProjects = await db.projects.toArray();
      setProjects(allProjects);

      // If no current entry, try to preload from the last time entry
      if (!currentTimeEntry && allProjects.length > 0) {
        const lastEntry = await db.getLastTimeEntry();
        if (lastEntry) {
          // Check if the project still exists
          const projectExists = allProjects.some(p => p.name === lastEntry.project);
          if (projectExists) {
            setSelectedProject(lastEntry.project);
            setSelectedTask(lastEntry.task || '');
            // Only set location if it's valid
            if (lastEntry.location === 'Site' || lastEntry.location === 'Shop') {
              setLocation(lastEntry.location);
            }
          } else {
            // Default to first project if last used project was deleted
            setSelectedProject(allProjects[0].name);
          }
        } else {
          // No history, default to first project
          setSelectedProject(allProjects[0].name);
        }
      } else if (allProjects.length > 0 && !selectedProject) {
        // Fallback if selectedProject is empty (shouldn't happen with above logic but good safety)
        setSelectedProject(allProjects[0].name);
      }

      if (currentTimeEntry) {
        setSelectedProject(currentTimeEntry.project);
        setSelectedTask(currentTimeEntry.task || '');
        setLocation(currentTimeEntry.location);
        setNotes(currentTimeEntry.notes || '');
      }
    } catch (error) {
      console.error('Error loading data:', error);
    }
  };

  const handleClockIn = async () => {
    try {
      // Check if project exists, if not create it
      if (selectedProject) {
        const existingProjects = await db.projects
          .where('name')
          .equals(selectedProject)
          .toArray();

        if (existingProjects.length === 0) {
          await db.projects.add({
            name: selectedProject,
            tasks: selectedTask ? [selectedTask] : []
          });
        } else if (selectedTask && !existingProjects[0].tasks.includes(selectedTask)) {
          await db.projects.update(existingProjects[0].id!, {
            tasks: [...existingProjects[0].tasks, selectedTask]
          });
        }
      }

      const userName = await db.getUserName();
      
      // Create time entry using the new addTimeEntry method
      await db.addTimeEntry({
        name: userName || 'Anonymous',
        timeIn: new Date(),
        project: selectedProject || 'Default Project',
        task: selectedTask,
        location,
        notes: notes || ''
      });

      await loadData();
      onStatusChange();
    } catch (error) {
      console.error('Error during clock in:', error);
    }
  };

  const handleClockOut = async () => {
    if (currentEntry?.id) {
      try {
        await db.updateTimeEntry(currentEntry.id, {
          timeOut: new Date(),
          notes: notes || currentEntry.notes || ''
        });

        setCurrentEntry(null);
        onStatusChange();

        // Send it now rather than waiting for someone to remember the Sync
        // button. Fire-and-forget: a failed sync must never block clocking
        // out, and the entry stays queued locally either way.
        autoSync().catch((err) => console.error('Auto-sync after clock-out failed:', err));
      } catch (error) {
        console.error('Error during clock out:', error);
      }
    }
  };

  const handleSwitch = async () => {
    if (currentEntry?.id) {
      try {
        // Clock out current entry using the new updateTimeEntry method
        await db.updateTimeEntry(currentEntry.id, {
          timeOut: new Date(),
          notes: notes || currentEntry.notes || ''
        });

        // Check if project exists, if not create it
        if (selectedProject) {
          const existingProjects = await db.projects
            .where('name')
            .equals(selectedProject)
            .toArray();

          if (existingProjects.length === 0) {
            await db.projects.add({
              name: selectedProject,
              tasks: selectedTask ? [selectedTask] : []
            });
          } else if (selectedTask && !existingProjects[0].tasks.includes(selectedTask)) {
            await db.projects.update(existingProjects[0].id!, {
              tasks: [...existingProjects[0].tasks, selectedTask]
            });
          }
        }

        // Create new time entry using the new addTimeEntry method
        await db.addTimeEntry({
          name: currentEntry.name,
          timeIn: new Date(),
          project: selectedProject || 'Default Project',
          task: selectedTask,
          location,
          notes: ''
        });

        setNotes('');
        await loadData();
        onStatusChange();
      } catch (error) {
        console.error('Error during switch:', error);
      }
    }
  };

  const handleProjectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const projectName = e.target.value;
    setSelectedProject(projectName);
    
    // Set location based on the selected project's location property
    if (projectName) {
      const selectedProj = projects.find(p => p.name === projectName);
      if (selectedProj && selectedProj.location) {
        setLocation(selectedProj.location);
      }
    }
    setSelectedTask('');
  };

  const handleAddProject = async () => {
    const newProject = prompt('Enter new project name:');
    if (newProject && newProject.trim()) {
      const projectName = newProject.trim();
      try {
        // Check if project already exists
        const existingProjects = await db.projects
          .where('name')
          .equals(projectName)
          .toArray();
        
        if (existingProjects.length === 0) {
          // Add new project to database
          await db.projects.add({
            name: projectName,
            tasks: []
          });
          
          // Reload projects from database
          const allProjects = await db.projects.toArray();
          setProjects(allProjects);
          setSelectedProject(projectName);
        } else {
          // Project already exists, just select it
          setSelectedProject(projectName);
        }
      } catch (error) {
        console.error('Error adding project:', error);
        alert('Failed to add project. Please try again.');
      }
    }
  };

  return (
    <div className="clock-controls">
      <div className="form-row">
        <div className="form-group">
          <label>Project:</label>
          <div className="select-with-button">
            <select
              value={selectedProject}
              onChange={handleProjectChange}
              disabled={!!currentEntry}
            >
              <option value="">Select Project</option>
              {projects.map((project) => (
                <option key={project.name} value={project.name}>
                  {project.name}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleAddProject}
              disabled={!!currentEntry}
              className="add-button"
            >
              +
            </button>
          </div>
        </div>

        <div className="form-group">
          <label>Task:</label>
          <select
            value={selectedTask}
            onChange={(e) => setSelectedTask(e.target.value)}
            disabled={!!currentEntry || !selectedProject}
          >
            <option value="">Select Task</option>
            {tasks.map((task) => (
              <option key={task} value={task}>{task}</option>
            ))}
          </select>
          {!selectedProject && !currentEntry && (
            <p className="helper-text">Select a project first</p>
          )}
        </div>
      </div>

      <div className="form-row">
        {showLocation && (
          <div className="form-group location-group">
            <label>Location:</label>
            <div className="location-toggle">
              <button
                type="button"
                className={location === 'Site' ? 'active' : ''}
                onClick={() => setLocation('Site')}
                disabled={!!currentEntry}
              >
                Site
              </button>
              <button
                type="button"
                className={location === 'Shop' ? 'active' : ''}
                onClick={() => setLocation('Shop')}
                disabled={!!currentEntry}
              >
                Shop
              </button>
            </div>
          </div>
        )}

        <div className="form-group">
          <label>Notes:</label>
          <input
            type="text"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
          />
        </div>
      </div>

      <div className="clock-buttons">
        {!currentEntry && (
          <button
            type="button"
            className="clock-in-btn"
            onClick={handleClockIn}
            disabled={!selectedProject}
          >
            Clock In
          </button>
        )}

        {currentEntry && (
          <>
            <button
              type="button"
              className="clock-out-btn"
              onClick={handleClockOut}
            >
              Clock Out
            </button>
            <button
              type="button"
              className="switch-btn"
              onClick={handleSwitch}
              disabled={!selectedProject}
            >
              Switch
            </button>
          </>
        )}
      </div>
    </div>
  );
};

export default ClockControls;
